import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

export interface LoginResponse {
  success: boolean;
  token: string;
  user: {
    id: number;
    email: string;
    firstName?: string;
    lastName?: string;
    employeeId?: string;
    department?: string;
    roleId: number;
    permissions: string[];
  };
}

export interface UserProfile {
  userId: number;
  email: string;
  roleId: number;
  permissions: string[];
}

class AuthService {
  // Login with Microsoft access token
  async loginWithMicrosoft(accessToken: string): Promise<LoginResponse> {
    try {
      const response = await axios.post<LoginResponse>(`${API_BASE_URL}/auth/login`, {
        accessToken
      });
      
      if (response.data.success && response.data.token) {
        // Store the JWT token
        localStorage.setItem('jwtToken', response.data.token);
        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      
      return response.data;
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(error.response?.data?.error || 'Login failed');
    }
  }

  // Logout
  async logout(): Promise<void> {
    try {
      const token = localStorage.getItem('jwtToken');
      if (token) {
        await axios.post(`${API_BASE_URL}/auth/logout`, {}, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage
      localStorage.removeItem('jwtToken');
      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('user');
    }
  }

  // Get current user profile
  async getProfile(): Promise<UserProfile> {
    const token = localStorage.getItem('jwtToken');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get<{ user: UserProfile }>(`${API_BASE_URL}/auth/profile`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    return response.data.user;
  }

  // Validate token
  async validateToken(): Promise<boolean> {
    try {
      const token = localStorage.getItem('jwtToken');
      if (!token) {
        return false;
      }

      const response = await axios.get<{ valid: boolean }>(`${API_BASE_URL}/auth/validate`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      return response.data.valid === true;
    } catch (error) {
      return false;
    }
  }

  // Get stored user
  getStoredUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  // Check if user is logged in
  isAuthenticated(): boolean {
    return localStorage.getItem('isLoggedIn') === 'true' && !!localStorage.getItem('jwtToken');
  }
}

export default new AuthService();
